import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from "react";
import { API } from "@/lib/api";

/**
 * Live updates over Server-Sent Events.
 *
 * The backend bumps a version on every write and pushes it to all clients.
 * When a new version arrives we call every subscribed page's reload function —
 * BUT we defer that reload while the user is actively typing (a text field is
 * focused) or while a refresh has been explicitly paused. The deferred refresh
 * runs the moment they finish, so a half-typed comment / description / remark is
 * never wiped out by someone else's change landing.
 */
const LiveRefreshContext = createContext(null);

export function LiveRefreshProvider({ children }) {
    const listeners = useRef(new Set());
    const pauseCount = useRef(0);
    const pending = useRef(false);
    const lastV = useRef(0);

    const fire = useCallback(() => {
        listeners.current.forEach((cb) => {
            try {
                cb();
            } catch {
                /* a single bad listener shouldn't break the rest */
            }
        });
    }, []);

    const release = useCallback(() => {
        if (pauseCount.current === 0 && pending.current) {
            pending.current = false;
            fire();
        }
    }, [fire]);

    const onVersion = useCallback(
        (v) => {
            if (typeof v !== "number" || v <= lastV.current) return;
            lastV.current = v;
            if (pauseCount.current > 0) {
                pending.current = true; // apply when the user is done editing
                return;
            }
            fire();
        },
        [fire],
    );

    // --- SSE connection (EventSource auto-reconnects on its own) ---
    useEffect(() => {
        const token = localStorage.getItem("sdc_token");
        if (!token) return;
        const es = new EventSource(`${API}/events?auth=${encodeURIComponent(token)}`);
        es.onmessage = (e) => {
            try {
                const d = JSON.parse(e.data);
                onVersion(d.v);
            } catch {
                /* ignore malformed frames */
            }
        };
        return () => es.close();
    }, [onVersion]);

    // --- Auto-pause while a text field is focused (i.e. the user is typing) ---
    useEffect(() => {
        const isField = (el) =>
            el &&
            (el.tagName === "INPUT" ||
                el.tagName === "TEXTAREA" ||
                el.isContentEditable);
        const onFocusIn = (e) => {
            if (isField(e.target)) pauseCount.current += 1;
        };
        const onFocusOut = (e) => {
            if (isField(e.target)) {
                pauseCount.current = Math.max(0, pauseCount.current - 1);
                release();
            }
        };
        document.addEventListener("focusin", onFocusIn);
        document.addEventListener("focusout", onFocusOut);
        return () => {
            document.removeEventListener("focusin", onFocusIn);
            document.removeEventListener("focusout", onFocusOut);
        };
    }, [release]);

    const ctx = useRef(null);
    if (!ctx.current) {
        ctx.current = {
            subscribe(cb) {
                listeners.current.add(cb);
                return () => listeners.current.delete(cb);
            },
            // Explicit guard for non-text interactions (e.g. an open dialog).
            pause() {
                pauseCount.current += 1;
            },
            resume() {
                pauseCount.current = Math.max(0, pauseCount.current - 1);
                release();
            },
        };
    }

    return (
        <LiveRefreshContext.Provider value={ctx.current}>
            {children}
        </LiveRefreshContext.Provider>
    );
}

/** Register a reload callback that runs (live) whenever data changes elsewhere. */
export function useLiveRefresh(onChange) {
    const ctx = useContext(LiveRefreshContext);
    const ref = useRef(onChange);
    ref.current = onChange;
    useEffect(() => {
        if (!ctx) return undefined;
        return ctx.subscribe(() => ref.current && ref.current());
    }, [ctx]);
}

/** Pause/resume live refresh imperatively (e.g. while a form dialog is open). */
export function useLiveRefreshControls() {
    const ctx = useContext(LiveRefreshContext);
    return {
        pause: ctx?.pause || (() => {}),
        resume: ctx?.resume || (() => {}),
    };
}
